import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

export const FloorStockManager: React.FC = () => {
  const data = useSelector((state: RootState) => state.newFloor.allFloorItems);
  // Track open/collapsed state by unique rawMaterialId
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return data.length > 0 ? (
    <div className="w-full max-w-5xl mx-auto p-4 bg-gray-50 min-h-screen">
      {/* --- MOBILE CARD VIEW (Visible only on small screens) --- */}
      <div className="block md:hidden space-y-4">
        {data.map((material) => {
          const isExpanded = !!expandedRows[material.rawMaterialId];
          return (
            <div
              key={material.rawMaterialId}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all"
            >
              {/* Card Header (Clickable) */}
              <div
                onClick={() => toggleRow(material.rawMaterialId)}
                className="p-4 flex justify-between items-center cursor-pointer active:bg-gray-50 select-none"
              >
                <div>
                  <h3 className="font-semibold text-gray-800 text-base">
                    {material.materialName}
                  </h3>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">
                    ID: {material.rawMaterialId.slice(-6)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="text-lg font-bold text-indigo-600">
                      {material.totalQuantity}
                    </span>
                    <span className="text-xs font-medium text-gray-500 ml-1">
                      {material.unit}
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {/* Card Collapsible Content */}
              {isExpanded && (
                <div className="bg-gray-50 border-t border-gray-100 p-4 space-y-3">
                  {material.requests.map((req, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-lg border border-gray-100 shadow-xs space-y-2"
                    >
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-gray-700">
                          Req No: #{req.requestNo}
                        </span>
                        <span className="font-bold text-gray-800">
                          {req.quantity} {req.unit}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-y-1 text-xs text-gray-500">
                        <div>
                          <span className="text-gray-400">Batch:</span>{" "}
                          {req.batchNo ?? "—"}
                        </div>
                        <div>
                          <span className="text-gray-400">User:</span>{" "}
                          {req.requestedBy}
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-400">Date:</span>{" "}
                          {formatDate(req.date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* --- DESKTOP TABLE VIEW (Hidden on mobile) --- */}
      <div className="hidden md:block overflow-hidden bg-white border border-gray-200 rounded-xl shadow-xs">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
            <tr>
              <th className="p-4 w-12"></th>
              <th className="p-4">Material Details</th>
              <th className="p-4">Total Qty On Floor</th>
              <th className="p-4 text-right">Active Requests</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((material) => {
              const isExpanded = !!expandedRows[material.rawMaterialId];
              return (
                <React.Fragment key={material.rawMaterialId}>
                  {/* Master Row */}
                  <tr
                    onClick={() => toggleRow(material.rawMaterialId)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="p-4 text-center">
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 mx-auto ${isExpanded ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </td>
                    <td className="p-4 font-medium text-gray-900">
                      {material.materialName}
                      <span className="block text-xs text-gray-400 font-normal font-mono mt-0.5">
                        {material.rawMaterialId}
                      </span>
                    </td>
                    <td className="p-4 text-gray-700 font-semibold">
                      {material.totalQuantity}{" "}
                      <span className="text-xs font-normal text-gray-400">
                        {material.unit}
                      </span>
                    </td>
                    <td className="p-4 text-right pr-6">
                      <span className="px-2.5 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 rounded-full">
                        {material.requests.length}{" "}
                        {material.requests.length === 1
                          ? "request"
                          : "requests"}
                      </span>
                    </td>
                  </tr>

                  {/* Expandable Sub-table Row */}
                  {isExpanded && (
                    <tr className="bg-gray-50/50">
                      <td colSpan={4} className="p-4 pl-12">
                        <div className="overflow-hidden border border-gray-150 rounded-lg bg-white shadow-inner">
                          <table className="min-w-full text-xs text-gray-600 divide-y divide-gray-150">
                            <thead className="bg-gray-50/70 text-gray-400 font-medium">
                              <tr>
                                <th className="p-3">Req No</th>
                                <th className="p-3">Batch No</th>
                                <th className="p-3">Requested By</th>
                                <th className="p-3">Issue Date</th>
                                <th className="p-3 text-right">Quantity</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {material.requests.map((req, idx) => (
                                <tr key={idx} className="hover:bg-gray-50/30">
                                  <td className="p-3 font-medium text-gray-700">
                                    #{req.requestNo}
                                  </td>
                                  <td className="p-3 font-mono">
                                    {req.batchNo ?? "—"}
                                  </td>
                                  <td className="p-3">{req.requestedBy}</td>
                                  <td className="p-3">
                                    {formatDate(req.date)}
                                  </td>
                                  <td className="p-3 text-right font-semibold text-gray-800">
                                    {req.quantity} {req.unit}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
      <div className="max-w-sm mx-auto">
        <h3 className="text-base font-semibold text-gray-900">
          No Stock Records found
        </h3>
      </div>
    </div>
  );
};
